/**
 * Tela /templates/:id (TH-53258 / M3.E6.S1.3) — editor de template.
 *
 * Carrega via GET /api/templates/{id}, edita via PATCH, deleta via DELETE
 * (com tratamento explícito do 409 quando há `product_arts` referenciando
 * — TH-53256 garante esse comportamento), pré-visualiza via POST
 * /templates/{id}/preview (TH-53257) que devolve PNG inline.
 *
 * Ajuste vs DoD:
 * - O DoD inferido falava em `html_template` e `config` (JSON). O schema
 *   real (apps/api/.../schemas/template.py) não tem `config` nem
 *   `html_template`: usa `html`, `css` e `ai_prompt`. Implementamos o
 *   schema real — adaptar sem inventar campos é regra do prompt.
 * - Também há `width` e `height` (320–3840 / 320–2160). Expostos como
 *   inputs num "Avançado" — alteração é rara mas necessária pra templates
 *   verticais.
 * - `version` é incrementada server-side quando html/css/ai_prompt mudam
 *   (mostra read-only no header).
 *
 * Preview:
 * - O backend retorna `image/png` direto (Response inline). Convertemos
 *   o blob com `URL.createObjectURL` e exibimos em <img>. Em caso de
 *   504/503 mostramos a mensagem inline.
 * - Body fixo `{}` na primeira versão — designer pode iterar mexendo no
 *   template e clicando em "Pré-visualizar". Sample data customizado fica
 *   para uma subtask futura (escopo limitado).
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useAuth } from "@/hooks/useAuth";

const ROLES_AUTHORIZED = new Set([
  "super_admin",
  "tenant_admin",
  "operator",
  "catalog_operator",
]);

interface Area {
  id: string;
  name: string;
  slug: string;
}

interface AreasResponse {
  data: Area[];
  meta: { total: number };
}

interface TemplateRead {
  id: string;
  area_id: string | null;
  name: string;
  html: string;
  css: string;
  ai_prompt: string;
  width: number;
  height: number;
  version: number;
  is_default: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface UpdatePayload {
  name?: string;
  html?: string;
  css?: string;
  ai_prompt?: string;
  width?: number;
  height?: number;
  is_default?: boolean;
  active?: boolean;
  area_id?: string | null;
}

async function fetchTemplate(id: string): Promise<TemplateRead> {
  const res = await fetch(`/api/templates/${id}`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as TemplateRead;
}

async function fetchAreas(): Promise<AreasResponse> {
  const res = await fetch("/api/areas?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as AreasResponse;
}

async function updateTemplate(
  id: string,
  payload: UpdatePayload,
): Promise<TemplateRead> {
  const res = await fetch(`/api/templates/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new Error(formatDetail(body.detail) ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as TemplateRead;
}

interface DeleteError extends Error {
  status?: number;
}

async function deleteTemplate(id: string): Promise<void> {
  const res = await fetch(`/api/templates/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (res.ok || res.status === 204) return;
  const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
  const err: DeleteError = new Error(
    formatDetail(body.detail) ?? `HTTP ${res.status}`,
  );
  err.status = res.status;
  throw err;
}

async function previewTemplate(id: string): Promise<Blob> {
  const res = await fetch(`/api/templates/${id}/preview`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new Error(formatDetail(body.detail) ?? `HTTP ${res.status}`);
  }
  return await res.blob();
}

function formatDetail(detail: unknown): string | null {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (d && typeof d === "object" && "msg" in d) {
          return String((d as { msg: unknown }).msg);
        }
        return JSON.stringify(d);
      })
      .join("; ");
  }
  return JSON.stringify(detail);
}

interface FormState {
  name: string;
  area_id: string;
  html: string;
  css: string;
  ai_prompt: string;
  width: string;
  height: string;
  is_default: boolean;
  active: boolean;
}

function emptyForm(): FormState {
  return {
    name: "",
    area_id: "",
    html: "",
    css: "",
    ai_prompt: "",
    width: "1920",
    height: "1080",
    is_default: false,
    active: true,
  };
}

function formFromTemplate(t: TemplateRead): FormState {
  return {
    name: t.name,
    area_id: t.area_id ?? "",
    html: t.html,
    css: t.css,
    ai_prompt: t.ai_prompt,
    width: String(t.width),
    height: String(t.height),
    is_default: t.is_default,
    active: t.active,
  };
}

interface ValidationResult {
  ok: boolean;
  errors: Partial<Record<keyof FormState, string>>;
}

function validate(form: FormState): ValidationResult {
  const errors: Partial<Record<keyof FormState, string>> = {};
  if (!form.name.trim()) errors.name = "Nome obrigatório";
  else if (form.name.trim().length > 200)
    errors.name = "Nome muito longo (máx 200)";

  if (!form.html.trim()) errors.html = "HTML obrigatório";
  if (!form.ai_prompt.trim()) errors.ai_prompt = "AI prompt obrigatório";

  const w = Number(form.width);
  if (!Number.isFinite(w) || w < 320 || w > 3840)
    errors.width = "320 ≤ largura ≤ 3840";
  const h = Number(form.height);
  if (!Number.isFinite(h) || h < 320 || h > 2160)
    errors.height = "320 ≤ altura ≤ 2160";

  return { ok: Object.keys(errors).length === 0, errors };
}

/** Diff entre form atual e template original — só envia o que mudou. */
function buildPatch(form: FormState, original: TemplateRead): UpdatePayload {
  const patch: UpdatePayload = {};
  const trimmedName = form.name.trim();
  if (trimmedName !== original.name) patch.name = trimmedName;
  if (form.html !== original.html) patch.html = form.html;
  if (form.css !== original.css) patch.css = form.css;
  if (form.ai_prompt !== original.ai_prompt)
    patch.ai_prompt = form.ai_prompt;
  const w = Number(form.width);
  if (w !== original.width) patch.width = w;
  const h = Number(form.height);
  if (h !== original.height) patch.height = h;
  if (form.is_default !== original.is_default)
    patch.is_default = form.is_default;
  if (form.active !== original.active) patch.active = form.active;
  const newArea = form.area_id ? form.area_id : null;
  if (newArea !== original.area_id) patch.area_id = newArea;
  return patch;
}

export function TemplateEditPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams({ strict: false }) as { templateId?: string };
  const templateId = params.templateId ?? "";

  const canManage = user ? ROLES_AUTHORIZED.has(user.role) : false;

  const templateQuery = useQuery({
    queryKey: ["template", templateId],
    queryFn: () => fetchTemplate(templateId),
    enabled: !!templateId && canManage,
  });

  const areasQuery = useQuery({
    queryKey: ["areas", "for-template-edit"],
    queryFn: fetchAreas,
    staleTime: 5 * 60_000,
    enabled: canManage,
    retry: false,
  });

  const [form, setForm] = useState<FormState>(emptyForm);
  const [hydrated, setHydrated] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (templateQuery.data && !hydrated) {
      setForm(formFromTemplate(templateQuery.data));
      setHydrated(true);
    }
  }, [templateQuery.data, hydrated]);

  // Limpa Object URLs antigos ao trocar/desmontar — evita leak de blobs.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const validation = useMemo(() => validate(form), [form]);
  const original = templateQuery.data;
  const patch = original ? buildPatch(form, original) : {};
  const hasChanges = Object.keys(patch).length > 0;

  const save = useMutation({
    mutationFn: async () => {
      if (!original) throw new Error("template não carregado");
      return updateTemplate(templateId, patch);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      queryClient.setQueryData(["template", templateId], data);
      setForm(formFromTemplate(data));
      setServerError(null);
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const remove = useMutation({
    mutationFn: () => deleteTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      navigate({ to: "/templates" });
    },
    onError: (err: DeleteError) => {
      if (err.status === 409) {
        setServerError(
          err.message ||
            "Template está em uso por artes — desative em vez de deletar.",
        );
      } else {
        setServerError(err.message);
      }
    },
  });

  const preview = useMutation({
    mutationFn: () => previewTemplate(templateId),
    onSuccess: (blob) => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      setPreviewError(null);
    },
    onError: (err: Error) => {
      setPreviewError(err.message);
    },
  });

  if (user && !canManage) {
    return (
      <RequireAuth>
        <AppLayout
          breadcrumb="OPERAÇÃO / TEMPLATES / EDIT"
          title="Acesso negado"
        >
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Esta tela está disponível para super_admin, tenant_admin,
              operator ou catalog_operator.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  const isLoading = templateQuery.isLoading;
  const loadError = templateQuery.error;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    if (!validation.ok || !hasChanges) return;
    save.mutate();
  }

  const areasList = areasQuery.data?.data ?? [];
  const headerTitle = original?.name ?? "Editor de template";

  const headerActions = (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => preview.mutate()}
        disabled={preview.isPending || !original || hasChanges}
        title={
          hasChanges
            ? "Salve as alterações antes de pré-visualizar"
            : undefined
        }
      >
        {preview.isPending ? "Renderizando…" : "Pré-visualizar"}
      </Button>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        onClick={() => setConfirmDelete(true)}
        disabled={!original || remove.isPending}
      >
        Excluir
      </Button>
      <Button
        type="submit"
        form="template-edit-form"
        size="sm"
        disabled={
          !validation.ok ||
          !hasChanges ||
          save.isPending ||
          remove.isPending
        }
      >
        {save.isPending ? "Salvando…" : "Salvar"}
      </Button>
    </div>
  );

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / TEMPLATES / EDIT"
        title={headerTitle}
        actions={headerActions}
      >
        <div className="mx-auto max-w-6xl">
          {loadError ? (
            <div className="mb-4 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]">
              erro ao carregar template
            </div>
          ) : null}

          {isLoading || !original ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {isLoading ? "carregando…" : "template não disponível"}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
              <form
                id="template-edit-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-6"
                noValidate
              >
                <Card className="p-4">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="tpl-name">Nome</Label>
                      <Input
                        id="tpl-name"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        maxLength={200}
                        aria-invalid={
                          validation.errors.name ? true : undefined
                        }
                      />
                      {validation.errors.name ? (
                        <p
                          role="alert"
                          className="text-[11px] text-[var(--color-destructive)]"
                        >
                          {validation.errors.name}
                        </p>
                      ) : null}
                    </div>

                    <div className="flex flex-col gap-2">
                      <Label htmlFor="tpl-area">Área</Label>
                      <Select
                        id="tpl-area"
                        value={form.area_id}
                        onChange={(e) => update("area_id", e.target.value)}
                        disabled={
                          areasQuery.isLoading || !areasQuery.isSuccess
                        }
                      >
                        <option value="">— sem área —</option>
                        {areasList.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </Select>
                      {areasQuery.error ? (
                        <p className="text-[11px] text-[var(--color-muted-foreground)]">
                          não foi possível carregar áreas — campo desativado
                        </p>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap items-center gap-6 pt-2">
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.active}
                          onChange={(e) => update("active", e.target.checked)}
                          className="h-4 w-4 cursor-pointer accent-[var(--color-primary)]"
                        />
                        Ativo
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.is_default}
                          onChange={(e) =>
                            update("is_default", e.target.checked)
                          }
                          className="h-4 w-4 cursor-pointer accent-[var(--color-primary)]"
                        />
                        Padrão da área
                      </label>
                      <span className="text-[11px] text-[var(--color-muted-foreground)]">
                        versão atual:{" "}
                        <span className="font-mono">v{original.version}</span>
                      </span>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold">html_template</h2>
                    <span className="text-[11px] text-[var(--color-muted-foreground)]">
                      {form.html.length.toLocaleString("pt-BR")} chars
                    </span>
                  </div>
                  <textarea
                    value={form.html}
                    onChange={(e) => update("html", e.target.value)}
                    rows={14}
                    spellCheck={false}
                    className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-3 font-mono text-xs text-[var(--color-foreground)] focus:border-[var(--color-primary)] focus:outline-none"
                    aria-invalid={validation.errors.html ? true : undefined}
                  />
                  {validation.errors.html ? (
                    <p
                      role="alert"
                      className="mt-1 text-[11px] text-[var(--color-destructive)]"
                    >
                      {validation.errors.html}
                    </p>
                  ) : null}
                </Card>

                <Card className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold">css</h2>
                    <span className="text-[11px] text-[var(--color-muted-foreground)]">
                      {form.css.length.toLocaleString("pt-BR")} chars
                    </span>
                  </div>
                  <textarea
                    value={form.css}
                    onChange={(e) => update("css", e.target.value)}
                    rows={10}
                    spellCheck={false}
                    className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-3 font-mono text-xs text-[var(--color-foreground)] focus:border-[var(--color-primary)] focus:outline-none"
                  />
                </Card>

                <Card className="p-4">
                  <div className="mb-3">
                    <h2 className="text-sm font-semibold">ai_prompt</h2>
                    <p className="mt-0.5 text-[11px] text-[var(--color-muted-foreground)]">
                      Instruções para o gerador de fundo (camada 1 do cache —
                      mudar invalida fundos cacheados).
                    </p>
                  </div>
                  <textarea
                    value={form.ai_prompt}
                    onChange={(e) => update("ai_prompt", e.target.value)}
                    rows={6}
                    spellCheck={false}
                    className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-3 font-mono text-xs text-[var(--color-foreground)] focus:border-[var(--color-primary)] focus:outline-none"
                    aria-invalid={
                      validation.errors.ai_prompt ? true : undefined
                    }
                  />
                  {validation.errors.ai_prompt ? (
                    <p
                      role="alert"
                      className="mt-1 text-[11px] text-[var(--color-destructive)]"
                    >
                      {validation.errors.ai_prompt}
                    </p>
                  ) : null}
                </Card>

                <Card className="p-4">
                  <h2 className="mb-3 text-sm font-semibold">Dimensões</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="tpl-width">Largura (px)</Label>
                      <Input
                        id="tpl-width"
                        type="number"
                        min={320}
                        max={3840}
                        value={form.width}
                        onChange={(e) => update("width", e.target.value)}
                        aria-invalid={
                          validation.errors.width ? true : undefined
                        }
                      />
                      {validation.errors.width ? (
                        <p
                          role="alert"
                          className="text-[11px] text-[var(--color-destructive)]"
                        >
                          {validation.errors.width}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="tpl-height">Altura (px)</Label>
                      <Input
                        id="tpl-height"
                        type="number"
                        min={320}
                        max={2160}
                        value={form.height}
                        onChange={(e) => update("height", e.target.value)}
                        aria-invalid={
                          validation.errors.height ? true : undefined
                        }
                      />
                      {validation.errors.height ? (
                        <p
                          role="alert"
                          className="text-[11px] text-[var(--color-destructive)]"
                        >
                          {validation.errors.height}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </Card>

                {serverError ? (
                  <div
                    role="alert"
                    className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
                  >
                    {serverError}
                  </div>
                ) : null}

                <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate({ to: "/templates" })}
                    disabled={save.isPending || remove.isPending}
                  >
                    ← Voltar
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      !validation.ok ||
                      !hasChanges ||
                      save.isPending ||
                      remove.isPending
                    }
                  >
                    {save.isPending
                      ? "Salvando…"
                      : hasChanges
                        ? "Salvar alterações"
                        : "Sem alterações"}
                  </Button>
                </div>
              </form>

              <aside className="flex flex-col gap-3">
                <Card className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold">Preview</h2>
                    {preview.isPending ? (
                      <span className="text-[11px] text-[var(--color-muted-foreground)]">
                        renderizando…
                      </span>
                    ) : null}
                  </div>

                  {previewError ? (
                    <div
                      role="alert"
                      className="mb-3 rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-3 py-2 text-[11px] text-[var(--color-destructive)]"
                    >
                      {previewError}
                    </div>
                  ) : null}

                  {previewUrl ? (
                    <div className="overflow-hidden rounded-md border border-[var(--color-border)] bg-black">
                      <img
                        src={previewUrl}
                        alt="Pré-visualização do template"
                        className="block h-auto w-full"
                      />
                    </div>
                  ) : (
                    <div className="flex aspect-video items-center justify-center rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-background)]/40 px-3 text-center text-[11px] text-[var(--color-muted-foreground)]">
                      {hasChanges
                        ? "Salve as alterações para pré-visualizar."
                        : "Clique em \"Pré-visualizar\" para gerar um PNG com produto exemplo."}
                    </div>
                  )}

                  <p className="mt-3 text-[10px] leading-relaxed text-[var(--color-muted-foreground)]">
                    O preview usa um produto sintético (Produto Exemplo, R$
                    9,99) e é cacheado por 5 minutos no servidor (ADR-019).
                  </p>
                </Card>
              </aside>
            </div>
          )}
        </div>

        <Dialog
          open={confirmDelete && !!original}
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
        >
          <DialogContent>
            <DialogTitle>Excluir template</DialogTitle>
            <DialogDescription>
              Confirma a exclusão de{" "}
              <strong className="text-[var(--color-foreground)]">
                {original?.name}
              </strong>
              ? O backend bloqueia (HTTP 409) se já existirem artes
              referenciando este template — nesse caso, desative em vez de
              deletar.
            </DialogDescription>
            {serverError ? (
              <p
                role="alert"
                className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
              >
                {serverError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setConfirmDelete(false)}
                disabled={remove.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                {remove.isPending ? "Excluindo…" : "Excluir"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </AppLayout>
    </RequireAuth>
  );
}
